import type { DesignTokens } from "../types/theme";

/**
 * ── Brand typography: Bible Strong Offline ──────────────────────────────
 * UI sans (Outfit) for chrome, navigation, and controls; reading serif
 * (EB Garamond) reserved for biblical text and concordance. Fonts
 * imported in `apps/web/index.html` and exposed as `--font-sans` /
 * `--font-serif` en `global.css`.
 */
const UI_SANS_FONT =
  "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * Brand color identity of Bible Strong Offline.
 *
 * Light = "library parchment": warm ivory, deep brown ink,
 * burgundy accent and aged gold. Reverent, scholarly, comfortable for reading
 * prolongada.
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  // ── Identity 2.0: closer to ivory/ink of published Play app
  // (`#FFFDF5` / `#280C0C`) while keeping the fleet warm parchment feel.
  background: "#faf3e2",
  foreground: "#2b1614",
  surface: "#fdf9ef",
  surfaceForeground: "#2b1614",
  surfaceSecondary: "#efe5cf",
  surfaceSecondaryForeground: "#2b1614",
  surfaceTertiary: "#e7dabf",
  surfaceTertiaryForeground: "#2b1614",
  overlay: "#fbf6ea",
  overlayForeground: "#2b1614",
  muted: "#8a7a63",
  default: "#efe5cf",
  defaultForeground: "#2b1614",
  accent: "#7c2d3a",
  accentForeground: "#fbf6ea",
  danger: "#a8323b",
  dangerForeground: "#fbf6ea",
  warning: "#a9821f",
  warningForeground: "#2b1614",
  success: "#4f6b3a",
  successForeground: "#fbf6ea",
  separator: "#d8c8a8",
  glassBackground: "rgba(251, 246, 234, 0.85)",
  glassBorder: "rgba(43, 22, 20, 0.12)",
  glassHighlight: "rgba(43, 22, 20, 0.06)",
  glow0: "rgba(124, 45, 58, 0.10)",
  glow1: "rgba(169, 130, 31, 0.10)",
  glow2: "rgba(79, 107, 58, 0.06)",
  mutedForeground: "#6b5d49",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(43, 22, 20, 0.10)",
  overlayShadow: "rgba(43, 22, 20, 0.12)",
  fieldShadow: "rgba(43, 22, 20, 0.08)",
  fieldBorder: "rgba(43, 22, 20, 0.12)",
  fieldPlaceholder: "rgba(43, 22, 20, 0.4)",
  fieldBackground: "rgba(43, 22, 20, 0.03)",
  segment: "#fbf6ea",
  segmentForeground: "#2b1614",
  link: "#7c2d3a",
  focus: "#a9821f",
  fieldForeground: "#2b1614",
  radius: "0.625rem",
  fontNormal: UI_SANS_FONT,
  fontMedium: UI_SANS_FONT,
  fontSemiBold: UI_SANS_FONT,
  fontBold: UI_SANS_FONT,
  border: "#d8c8a8",
  primary: "#7c2d3a",
  primaryForeground: "#fbf6ea",
  secondary: "#efe5cf",
  secondaryForeground: "#2b1614",
  backdrop: "rgba(26, 20, 16, 0.55)",
  // Strong blue from published app (measured `#002F84` in capture 03),
  // lightened just enough for 9.8:1 on `#faf3e2` and >8:1 on the 10 pastels.
  strong: "#0d3a86",
  // Words of Jesus: deep red legible on ivory and on pastel.
  redLetter: "#a32219",
};

/**
 * Dark = "study night": very dark ink brown, subdued parchment text,
 * softened burgundy accent and luminous aged gold. Comfortable for reading at
 * noche sin fatiga.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#16110d",
  foreground: "#ece3d2",
  surface: "#211a14",
  surfaceForeground: "#ece3d2",
  surfaceSecondary: "#2a2118",
  surfaceSecondaryForeground: "#ece3d2",
  surfaceTertiary: "#352a1f",
  surfaceTertiaryForeground: "#ece3d2",
  overlay: "#241c15",
  overlayForeground: "#ece3d2",
  muted: "#9c8d76",
  default: "#332819",
  defaultForeground: "#ece3d2",
  accent: "#c97b86",
  accentForeground: "#1a120c",
  danger: "#d97a82",
  dangerForeground: "#1a120c",
  warning: "#d6b25a",
  warningForeground: "#1a120c",
  success: "#9ab17e",
  successForeground: "#1a120c",
  separator: "#3d3122",
  glassBackground: "rgba(22, 17, 13, 0.92)",
  glassBorder: "rgba(236, 227, 210, 0.12)",
  glassHighlight: "rgba(236, 227, 210, 0.18)",
  glow0: "rgba(201, 123, 134, 0.32)",
  glow1: "rgba(214, 178, 90, 0.28)",
  glow2: "rgba(154, 177, 126, 0.20)",
  mutedForeground: "#bdae96",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.4)",
  overlayShadow: "rgba(0, 0, 0, 0.45)",
  fieldShadow: "rgba(0, 0, 0, 0.3)",
  fieldBorder: "rgba(236, 227, 210, 0.12)",
  fieldPlaceholder: "rgba(236, 227, 210, 0.4)",
  fieldBackground: "rgba(236, 227, 210, 0.04)",
  segment: "#3d3122",
  segmentForeground: "#ece3d2",
  link: "#c97b86",
  focus: "#d6b25a",
  fieldForeground: "#ece3d2",
  radius: "0.625rem",
  fontNormal: UI_SANS_FONT,
  fontMedium: UI_SANS_FONT,
  fontSemiBold: UI_SANS_FONT,
  fontBold: UI_SANS_FONT,
  border: "#3d3122",
  primary: "#c97b86",
  primaryForeground: "#1a120c",
  secondary: "#332819",
  secondaryForeground: "#ece3d2",
  backdrop: "rgba(0, 0, 0, 0.7)",
  // Light Strong blue in dark mode (measured `#ABC6E1` in capture 02).
  strong: "#a9c6e8",
  // Salmon for Words of Jesus in dark mode (legacy `Colors.red40`).
  redLetter: "#f2938b",
};
